<template>
  <v-dialog
    :value="value"
    max-width="640px"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        <span class="headline">
          {{ editMode ? "تعديل تصنيف" : "إضافة تصنيف" }}
        </span>
      </v-card-title>

      <v-card-text>
        <v-progress-linear
          v-if="fetching"
          indeterminate
          color="primary"
          class="mb-4"
          rounded
        />

        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="form.value"
                label="التصنيف"
                placeholder="اكتب محتوى التصنيف هنا"
                :rules="[rules.required]"
                outlined
                dense
                required
              />
            </v-col>

            <!-- اللون: color picker (badge background) -->
            <v-col cols="12" md="4">
              <div class="color-field">
                <span class="color-field__label">اللون</span>
                <v-menu
                  v-model="colorMenu"
                  :close-on-content-click="false"
                  offset-y
                  transition="scale-transition"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="color-swatch-btn"
                      :style="{ backgroundColor: form.color || '#757575' }"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small color="white">mdi-eyedropper-variant</v-icon>
                    </button>
                  </template>
                  <v-sheet class="pa-2">
                    <!-- paste/type a hex code directly -->
                    <v-text-field
                      v-model="hexInput"
                      @input="commitHex"
                      label="كود اللون"
                      placeholder="#757575"
                      prepend-inner-icon="mdi-pound"
                      maxlength="7"
                      outlined
                      dense
                      hide-details
                      class="hex-input mb-2"
                    />
                    <v-color-picker
                      :value="colorModel"
                      @input="onColorInput"
                      mode="hex"
                      hide-mode-switch
                      flat
                    />
                    <!-- own preset palette (Vuetify's show-swatches renders blank under this app's dark CSS) -->
                    <div class="preset-swatches">
                      <button
                        v-for="c in presetColors"
                        :key="c"
                        type="button"
                        class="preset-swatch"
                        :class="{ 'preset-swatch--active': isActivePreset(c) }"
                        :style="{ backgroundColor: c }"
                        :title="c"
                        @click="selectPreset(c)"
                      ></button>
                    </div>
                    <div class="text-center mt-2">
                      <v-btn x-small text @click="clearColor">بدون لون</v-btn>
                    </div>
                  </v-sheet>
                </v-menu>
                <span class="color-field__value">{{ form.color || "#757575" }}</span>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="form.user_role"
                :items="roles"
                item-text="label"
                item-value="value"
                label="المستخدم"
                :rules="[rules.requiredSelect]"
                outlined
                dense
                required
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="form.type"
                :items="typeOptions"
                item-text="label"
                item-value="value"
                label="نوع التصنيف"
                outlined
                dense
              />
            </v-col>

            <!-- Only خدمة العملاء rows carry an equivalence: the الاستشاري row this
                 one means the same thing as. Rules keyed on ids (مرفوض, لم يرد,
                 أجل التواصل, ...) resolve through it. -->
            <v-col cols="12" md="12" v-if="isCustomerServiceRole">
              <v-autocomplete
                v-model="form.equivalent_id"
                :items="agentClassifications"
                :loading="loadingAgentClassifications"
                item-text="value"
                item-value="id"
                label="يعادل تصنيف الاستشاري"
                hint="يحدد سلوك التصنيف (سبب الرفض، تاريخ المتابعة، منع التكرار)"
                persistent-hint
                clearable
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="form.is_required_in_calculater"
                :items="yesNoOptions"
                item-text="label"
                item-value="value"
                label="متطلب للحسبة؟"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="form.is_automatic"
                :items="autoOptions"
                item-text="label"
                item-value="value"
                label="هل أوتوماتك؟"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="form.is_transferred_to_need_action"
                :items="yesNoOptions"
                item-text="label"
                item-value="value"
                label="هل يحول لسلة التحويل؟"
                outlined
                dense
              />
            </v-col>

            <!--
              Turns this classification into a rejection: the request file shows the
              أسباب الرفض dropdown and the server makes a reason mandatory. Works for
              any role — it replaced the hardcoded مرفوض id.
            -->
            <v-col cols="12" md="6">
              <v-select
                v-model="form.is_rejected"
                :items="yesNoOptions"
                item-text="label"
                item-value="value"
                label="يتطلب سبب رفض؟"
                outlined
                dense
              />
            </v-col>


            <!-- ================================================================================== -->
            <!-- هل نزسل تذكره تلقائيه -->
            <v-col cols="12" md="6">
              <v-select
                v-model="form.is_send_ticket_automatically"
                :items="yesNoOptions"
                item-text="label"
                item-value="value"
                label="هل نزسل تذكره تلقائيه؟"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" md="6" v-if="form.is_send_ticket_automatically == 1">
              <v-text-field
                v-model="form.ticket_content"
                label="المحتوى للتذكره"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6" v-if="form.is_send_ticket_automatically == 1">
              <v-select
                v-model="form.ticket_to"
                :items="[{ label: 'خدمه العملاء', value: 'customer_service' }, { label: 'الادمن', value: 'admin' }]"
                item-text="label"
                item-value="value"
                label="ارسالها لمن"
                outlined
                dense
              />
            </v-col>

            <!-- ================================================================================== -->

          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">إلغاء</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">حفظ</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Axios from "axios";

export default {
  name: "ClassificationDialog",
  props: {
    value: { type: Boolean, default: false },
    editId: { type: [Number, String], default: null },
    roles: { type: Array, default: () => [] },
  },
  data() {
    return {
      valid: false,
      saving: false,
      fetching: false,
      colorMenu: false,
      hexInput: "",
      presetColors: [
        "#E53935", "#D81B60", "#8E24AA", "#5E35B1", "#3949AB",
        "#1E88E5", "#039BE5", "#00ACC1", "#00897B", "#43A047",
        "#7CB342", "#C0CA33", "#FDD835", "#FFB300", "#FB8C00",
        "#F4511E", "#6D4C41", "#757575", "#546E7A", "#212121",
      ],
      form: this.blankForm(),
      typeOptions: [
        { label: "إيجابي", value: 1 },
        { label: "سلبي", value: 0 },
        { label: "محايد", value: 2 },
      ],
      yesNoOptions: [
        { label: "نعم", value: 1 },
        { label: "لا", value: 0 },
      ],
      autoOptions: [
        { label: "نعم", value: "yes" },
        { label: "لا", value: "no" },
      ],
      agentClassifications: [],
      loadingAgentClassifications: false,
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          "هذا الحقل مطلوب",
        requiredSelect: (v) =>
          (v !== null && v !== undefined && v !== "") || "هذا الحقل مطلوب",
      },
    };
  },
  computed: {
    apiBase() {
      return `${this.$store.state.url}/employee/admin/classifications`;
    },
    editMode() {
      return this.editId !== null && this.editId !== undefined;
    },
    // Always feed the picker a STRING. If form.color is null, v-color-picker
    // can't infer the type and would emit/return its full color object (which
    // breaks the swatch and dumps JSON in the template), so fall back to gray.
    colorModel() {
      return this.form.color || "#757575";
    },
    isCustomerServiceRole() {
      return String(this.form.user_role) === "cs";
    },
  },
  watch: {
    value(opened) {
      if (!opened) return;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
      if (this.editMode) {
        this.fetchClassification();
      } else {
        this.form = this.blankForm();
      }
    },
    "form.user_role"(role) {
      if (String(role) === "cs") {
        this.fetchAgentClassifications();
      } else {
        // equivalence only means something on a خدمة العملاء row
        this.form.equivalent_id = null;
      }
    },
    // keep the hex text field in sync when color is set via the picker,
    // a preset swatch, or loaded from an existing classification.
    "form.color"(val) {
      const norm = val || "";
      if (norm !== this.hexInput) this.hexInput = norm;
    },
  },
  methods: {
    blankForm() {
      return {
        value: "",
        color: null,
        user_role: null,
        type: 1,
        is_required_in_calculater: 0,
        is_automatic: "no",
        is_transferred_to_need_action: 0,
        is_rejected: 0,
        is_send_ticket_automatically: 0,
        ticket_content: "",
        ticket_to: "",
        equivalent_id: null,
      };
    },
    async fetchAgentClassifications() {
      if (this.agentClassifications.length || this.loadingAgentClassifications) return;
      this.loadingAgentClassifications = true;
      try {
        const res = await Axios.get(this.apiBase, {
          headers: this.request_headers(),
          params: { class_user: 0, per_page: 200 },
        });
        this.agentClassifications = res.data.payload.data || [];
      } catch (e) {
        this.agentClassifications = [];
      } finally {
        this.loadingAgentClassifications = false;
      }
    },
    async fetchClassification() {
      this.fetching = true;
      try {
        const res = await Axios.get(`${this.apiBase}/${this.editId}`, {
          headers: this.request_headers(),
        });
        const c = res.data.payload;
        this.form = {
          value: c.value || "",
          color: c.color || null,
          // 'cs' for customer-service rows, otherwise the numeric role
          user_role: c.user_role,
          // keep 0 (سلبي) / 1 (إيجابي) / 2 (محايد)
          type: [0, 1, 2].includes(c.type) ? c.type : 1,
          is_required_in_calculater: c.is_required_in_calculater ? 1 : 0,
          is_automatic: c.is_automatic === "yes" ? "yes" : "no",
          is_transferred_to_need_action: c.is_transferred_to_need_action ? 1 : 0,
          is_rejected: c.is_rejected ? 1 : 0,
          is_send_ticket_automatically: c.is_send_ticket_automatically ? 1 : 0,
          ticket_content: c.ticket_content || "",
          ticket_to: c.ticket_to || "",
          equivalent_id: c.equivalent_id ?? null,
        };
        if (this.isCustomerServiceRole) {
          this.fetchAgentClassifications();
        }
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "تعذر تحميل التصنيف");
        this.close();
      } finally {
        this.fetching = false;
      }
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const payload = {
          value: this.form.value,
          color: this.form.color,
          user_role: this.form.user_role,
          type: this.form.type,
          is_required_in_calculater: this.form.is_required_in_calculater,
          is_automatic: this.form.is_automatic,
          is_transferred_to_need_action: this.form.is_transferred_to_need_action,
          is_rejected: this.form.is_rejected,
          is_send_ticket_automatically: this.form.is_send_ticket_automatically,
          ticket_content: this.form.ticket_content,
          ticket_to: this.form.ticket_to,
          equivalent_id: this.isCustomerServiceRole ? this.form.equivalent_id : null,
        };

        if (this.editMode) {
          await Axios.post(`${this.apiBase}/${this.editId}`, payload, {
            headers: this.request_headers(),
          });
        } else {
          await Axios.post(this.apiBase, payload, {
            headers: this.request_headers(),
          });
        }

        this.showPopup(
          "success",
          this.editMode ? "تم تعديل التصنيف بنجاح" : "تمت إضافة التصنيف بنجاح"
        );
        this.$emit("saved");
        this.close();
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.saving = false;
      }
    },
    selectPreset(c) {
      this.form.color = c;
    },
    isActivePreset(c) {
      return (this.form.color || "").toUpperCase() === c.toUpperCase();
    },
    commitHex(raw) {
      // accept pasted/typed codes with or without "#", 3- or 6-digit hex.
      if (raw === null || raw === undefined) return;
      let v = String(raw).trim();
      if (v === "") {
        this.form.color = null;
        return;
      }
      if (v[0] !== "#") {
        v = "#" + v;
        // reflect the auto-added "#" back into the field
        this.hexInput = v;
      }
      if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) {
        this.form.color = v.toUpperCase();
      }
      // partial/invalid input: leave the picker untouched until it's valid
    },
    onColorInput(val) {
      // v-color-picker emits a hex string in `hex` mode, but can hand back its
      // full color object on edge transitions — normalize to a plain hex string.
      if (val === null || val === undefined || val === "") {
        this.form.color = null;
        return;
      }
      this.form.color =
        typeof val === "string" ? val : val.hexa || val.hex || null;
    },
    clearColor() {
      this.form.color = null;
      this.colorMenu = false;
    },
    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.color-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}
.color-field__label {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}
.color-field__value {
  font-size: 0.8rem;
  font-family: monospace;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.6);
}
.color-swatch-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}
.color-swatch-btn:hover {
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
}

/* hex paste/type field matches the picker width */
.hex-input {
  max-width: 300px;
}
.hex-input >>> input {
  font-family: monospace;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

/* own preset palette */
.preset-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 300px;
  margin-top: 10px;
}
.preset-swatch {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}
.preset-swatch:hover {
  transform: scale(1.12);
}
.preset-swatch--active {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #3f0e40;
}
.theme--dark .preset-swatch,
.dark .preset-swatch {
  border-color: rgba(255, 255, 255, 0.22);
}
.theme--dark .preset-swatch--active,
.dark .preset-swatch--active {
  box-shadow: 0 0 0 2px #1e1e1e, 0 0 0 4px #b388ff;
}

/* dark theme contrast (app uses both Vuetify's theme--dark and a custom .dark) */
.theme--dark .color-field__label,
.theme--dark .color-field__value,
.dark .color-field__label,
.dark .color-field__value {
  color: rgba(255, 255, 255, 0.7);
}
.theme--dark .color-swatch-btn,
.dark .color-swatch-btn {
  border-color: rgba(255, 255, 255, 0.24);
}
</style>
